/**
 * features/communications/TeacherParentComms.tsx
 * ------------------------------------------------------------------
 * Entry point for teaching staff to message the parents of their own
 * pupils. Resolves the signed-in user's Teacher record (via
 * linkedTeacherId), computes their reach (classes + pupils), and renders
 * the shared composer in teacher-scoped mode.
 *
 * Nav visibility is gated on canMessageParents (class/subject teacher,
 * HOY, HOD). This wrapper additionally guards the two states where the
 * composer would be meaningless: no linked teacher record, or a teacher
 * who currently reaches no pupils.
 * ------------------------------------------------------------------
 */
import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getLinkedTeacher } from '@/lib/permissions';
import { computeTeacherReach } from '@/lib/teacherReach';
import { SoftCard } from '@/components/ui/glass';
import ParentNoticesManager from '@/features/communications/ParentNoticesManager';

export default function TeacherParentComms() {
  const { userData } = useAuth();
  const { teachers, classes, students } = useData();

  const teacher = useMemo(
    () => getLinkedTeacher(userData, teachers),
    [userData, teachers],
  );

  const reach = useMemo(() => {
    if (!teacher) return null;
    return computeTeacherReach(
      {
        id: teacher.id,
        teachingAssignments: teacher.teachingAssignments,
        classAssignments: teacher.classAssignments,
        classTeacherOf: teacher.classTeacherOf,
        assistantClassTeacherOf: teacher.assistantClassTeacherOf,
      },
      classes.map((c) => ({
        id: c.id,
        form: c.form,
        classTeacherId: c.classTeacherId,
        assistantClassTeacherId: c.assistantClassTeacherId,
      })),
      students.map((s) => ({
        id: s.id,
        currentClass: s.currentClass,
        subjectTeachers: s.subjectTeachers,
      })),
    );
  }, [teacher, classes, students]);

  if (!teacher) {
    return (
      <SoftCard>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <AlertCircle size={16} />
          Your account isn't linked to a teacher record yet, so there are no pupils to message.
          Ask an administrator to link your staff profile.
        </div>
      </SoftCard>
    );
  }

  if (!reach || reach.studentIds.length === 0) {
    return (
      <SoftCard>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <AlertCircle size={16} />
          You don't currently teach or class-teach any pupils, so there are no parents to message.
        </div>
      </SoftCard>
    );
  }

  return (
    <ParentNoticesManager
      reach={reach}
      senderRole={userData?.role}
      senderDepartment={teacher.department ?? null}
    />
  );
}
